// Canva Clone Editor - Main Component
// Based on Apache 2.0 licensed Canva Clone, adapted for Poster Studio

import { API_BASE } from '../../../config.js';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas } from 'fabric';
import { createPortal } from 'react-dom';
import QRCode from 'qrcode';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../i18n/formatters';
import { useToast } from '../../../contexts/ToastContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import { useFeature } from '../../../contexts/FeatureContext';
import { useUpgradePrompt } from '../../../contexts/UpgradePromptContext';
import { useAuth } from '../../../contexts/AuthContext';

// Brightivity: Pexels search through brightivity-design (the key is a secret there); photo files come
// back as brightivity.dev links.
const PEXELS_PROXY_URL = `${API_BASE}/design/photos/search`;
import { useCanvasEditor, DEFAULT_WIDTH, DEFAULT_HEIGHT } from './hooks/useCanvasEditor';
import { loadEditorFonts } from './editorFonts';
import ContextMenu from './ContextMenu';
import SmartObjectsSidebar from './SmartObjectsSidebar';
// Smart Tool modal (Smart Tools epic, 2026-07-23): the ONE edit/insert surface
// for smart-object recipes — replaces the drawer's retired edit mode. Host
// mounts once below; every entry point (ribbon Edit button, canvas dblclick,
// drawer banner, SM-B's browser) opens it through the controller.
import SmartToolModalHost from './SmartToolModal';
import { openSmartToolModal } from './smartToolModalController';
import { getRecipe } from '../../../posterTools';
import { Wand2 } from 'lucide-react';
import { importFilesToEditor } from './lib/importFiles';
import RibbonMount from './ribbon/RibbonMount';
import InspectorPanel from './inspector/InspectorPanel';
// The left "Text" drawer (Add text / Decorative / 3D sections; owner rebuild
// 2026-07-22). Not lazy — it's a primary insert panel, loads with the editor.
import TextInsertPanel from './inspector/text/TextInsertPanel';
// RS lane: rulers + overlay scrollbars around the work surface (the fabric
// container rides INSIDE this chrome; prefs live in viewport/canvasPrefs.js).
import CanvasViewport from './viewport/CanvasViewport';
// dblclick-to-edit surface for placed 3D text. LAZY by law: no 3D surface
// code loads at editor boot — this chunk (and, inside it, the lazy
// Text3DPanel that pulls three) loads only when a text3d object is actually
// double-clicked.
const Text3DEditModal = React.lazy(() => import('./inspector/text/Text3DEditModal.jsx'));
// dblclick-to-edit surface for placed emboss text (2D Bevel & Emboss). Same
// lazy contract as the 3D modal; the emboss bake chunk loads only when a
// placed emboss object is actually double-clicked. (Component owned by the UI
// lane — this seam wires the exact path it publishes to.)
const EmbossEditModal = React.lazy(() => import('./inspector/text/EmbossEditModal.jsx'));
import { isTextType } from './hooks/useSelectionProps';
import {
  IconSnap, IconZoomIn, IconZoomOut, IconFit,
  IconPanelTemplates, IconPanelElements, IconPanelText, IconPanelMedia,
  IconPanelLayers, IconPanelDraw, IconPanelQr, IconPanelSettings,
} from './toolbarIcons';
import { exportWorkspaceBlob, createHandoffUrl, flattenRemoteImages } from './lib/exportUtils';
// Browser stand-ins for the desktop's Electron bridges (cloud library,
// localStorage drafts, wallet-proxied AI). See webPlatform.js.
import platform from './webPlatform';
import './StudioCoreEditor.css';
// (PDF import — pdf.js worker setup — lives in lib/importFiles.js.
//  Toolbar/Inspector primitives live in primitives/toolbarPrimitives.jsx.)

// Tool types
const TOOLS = {
  SELECT: 'select',
  TEMPLATES: 'templates',
  TEXT: 'text',
  ELEMENTS: 'elements', // Shapes | Icons
  MEDIA: 'media',       // Photos | Illustrations
  SMART_OBJECTS: 'smart-objects', // Parametric smart objects (poster-project recipes)
  LAYERS: 'layers',
  DRAW: 'draw',
  QRCODE: 'qrcode',
  SETTINGS: 'settings',
};

// Tool to Feature mapping for gating
// Maps each tool to its corresponding feature key in the print_editor component
const TOOL_FEATURE_MAP = {
  [TOOLS.TEMPLATES]: 'templates',
  [TOOLS.TEXT]: 'text',
  [TOOLS.MEDIA]: 'photos',     // Media panel (Photos + Illustrations + Upload)
  [TOOLS.ELEMENTS]: 'elements', // Elements panel (Shapes + Icons)
  [TOOLS.SMART_OBJECTS]: 'smart_objects', // Smart Objects panel (parametric recipes)
  [TOOLS.LAYERS]: 'layers',
  [TOOLS.DRAW]: 'draw',
  [TOOLS.QRCODE]: 'qr_code',
  [TOOLS.SETTINGS]: 'settings',
};

// Reusable Panel Tabs Component
const PanelTabs = ({ tabs, activeTab, onTabChange }) => (
  <div className="cce-panel-tabs">
    {tabs.map(tab => (
      <button
        key={tab.id}
        className={`cce-panel-tab ${activeTab === tab.id ? 'active' : ''}`}
        onClick={() => onTabChange(tab.id)}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// Selection dependent tools
const selectionDependentTools = ["fill", "font", "filter", "opacity", "stroke-color", "stroke-width"];

// Templates API URL — separate worker (`hp-designjet-templates`), not part of
// the auth-worker migration. Env-driven so a future custom-domain flip is a
// one-line app/.env change. See app/specifications/login/todo/custom-domain-migration.md §9.
// Brightivity: templates from brightivity-design (shared rows; files mirrored into Brightivity's R2).
const TEMPLATES_API_URL = `${API_BASE}/design`;

// Poster and Banner Presets for HP DesignJet printers (72 DPI)
const SIZE_PRESETS = [
  { label: '24"x36" Poster', kind: 'poster', width: 24, height: 36, widthPx: 1728, heightPx: 2592 },
  { label: '36"x54" Poster', kind: 'poster', width: 36, height: 54, widthPx: 2592, heightPx: 3888 },
  { label: '42"x63" Poster', kind: 'poster', width: 42, height: 63, widthPx: 3024, heightPx: 4536 },
  { label: '24"x72" Banner', kind: 'banner', width: 24, height: 72, widthPx: 1728, heightPx: 5184 },
  { label: '36"x108" Banner', kind: 'banner', width: 36, height: 108, widthPx: 2592, heightPx: 7776 },
];

// Save Design Modal Component
const SaveDesignModal = ({ isOpen, onClose, editor, initialDesignName, initialDesignId, onSaved }) => {
  const { t } = useTranslation('editor');
  const toast = useToast();
  const { confirm } = useConfirm();
  const [designName, setDesignName] = useState(initialDesignName || t('save.untitledName'));
  const isEditing = !!initialDesignId; // True if editing an existing design
  const [thumbnail, setThumbnail] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Seeded from the design's own sharpness target (Bar 0 Poster flyout —
  // workspace meta, default 300) so this modal and the app bar agree.
  const [printDPI, setPrintDPI] = useState(() => editor?.getExportDPI?.() ?? 300);

  // Update design name when initialDesignName changes (for editing existing designs)
  useEffect(() => {
    if (initialDesignName) {
      setDesignName(initialDesignName);
    }
  }, [initialDesignName]);

  useEffect(() => {
    if (isOpen && editor) {
      const dataUrl = editor.toDataURL({ pixelRatio: 0.5 });
      setThumbnail(dataUrl);
    }
  }, [isOpen, editor]);

  const handleSaveToLibrary = async () => {
    if (!editor) return;
    setIsExporting(true);

    try {
      // Self-contained saves: embed remote images (Pexels/stickers) so the
      // design reloads offline and never hits an expired URL.
      await flattenRemoteImages(editor.canvas);
      const json = editor.toJSON();
      const thumbnailData = editor.toDataURL({ pixelRatio: 1 });
      const widthPx = editor.getWidth();
      const heightPx = editor.getHeight();
      // Web save stores the flattened PNG in R2 as the design's image, so the
      // library card, Print and Send-for-Approval all have something to fetch.
      // A failed export must not block saving the JSON + thumbnail.
      let fullResBlob = null;
      try {
        const exported = await exportWorkspaceBlob(editor.canvas, editor.getWorkspace(), printDPI);
        fullResBlob = exported?.blob || null;
      } catch (err) {
        console.warn('[editor] full-res export failed, saving thumbnail only:', err?.message);
      }
      const widthInches = widthPx / 72;
      const heightInches = heightPx / 72;

      const metaData = {
        url: 'designEditor',
        platform: 'Design Editor',
        // R2 sync: editor-saved designs (including copies of purchased printables)
        // are user designs — tag 'manual' so they round-trip through the
        // user-designs store instead of being treated as a printables entitlement
        // (spec risk #7).
        source: 'manual',
        dimensions: { width: widthPx, height: heightPx, unit: 'px' },
        additionalInfo: {
          widthInches,
          heightInches,
          actualDPI: 72,
          actualPixelWidth: widthPx,
          actualPixelHeight: heightPx,
        },
      };

      if (isEditing && initialDesignId) {
        // Update existing design
        {
          const result = await platform.updateDesign(initialDesignId, {
            d_name: designName,
            d_thumbnail: thumbnailData,
            d_json: json,
            d_metaData: metaData,
            d_fullBlob: fullResBlob,
          });
          if (result.success) {
            toast.showSuccess(t('save.toast.updated'));
            onSaved?.({ json, designId: initialDesignId, designName });
            onClose();
          } else {
            toast.showError(t('save.toast.updateFailed', { error: result.error }));
          }
        }
      } else {
        // Create new design
        const design = {
          design_uuid: crypto.randomUUID(),
          d_name: designName,
          d_thumbnail: thumbnailData,
          d_pdf: '',
          d_fullBlob: fullResBlob,
          d_json: json,
          d_metaData: metaData,
          d_category: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        {
          const result = await platform.saveDesign(design);
          if (result.success) {
            toast.showSuccess(t('save.toast.saved'));
            onSaved?.({ json, designId: result.design?.id || design.design_uuid, designName });
            onClose();
          } else {
            toast.showError(t('save.toast.saveFailed', { error: result.error }));
          }
        }
      }
    } catch (error) {
      toast.showError(t('save.toast.saveError', { error: error.message }));
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenInPrint = async () => {
    if (!editor) return;

    // Print-quality gate: images placed larger than their pixels support
    // (< 150 effective DPI) will look soft or blurry on a large-format print.
    const lowRes = editor.getLowResImages?.(150) || [];
    if (lowRes.length > 0) {
      const worst = Math.min(...lowRes.map((r) => r.dpi));
      const proceed = await confirm(
        t('save.printQuality.message', { count: lowRes.length, dpi: worst }),
        { title: t('save.printQuality.title'), confirmText: t('save.printQuality.confirm'), cancelText: t('save.printQuality.cancel') }
      );
      if (!proceed) return;
    }

    setIsExporting(true);

    try {
      // DPI-targeted export (default 300, clamped to the device's canvas
      // ceiling) — replaces the old fixed pixelRatio:2 (144 DPI cap). Blob
      // URL instead of a giant base64 string; PrintPreview fetch()es it.
      const workspace = editor.getWorkspace();
      const canvasWidthPx = editor.getWidth();
      const canvasHeightPx = editor.getHeight();
      const { blob, actualDPI, widthPx, heightPx } = await exportWorkspaceBlob(
        editor.canvas,
        workspace,
        printDPI,
      );

      window.pendingBrowserDownload = {
        base64: createHandoffUrl(blob),
        // Web: WebPrintPreview consumes a real Blob (same-origin, so the
        // preview canvas stays untainted and Download works).
        blob,
        name: designName,
        metadata: {
          url: 'designEditor',
          platform: 'Design Editor',
          dimensions: { width: canvasWidthPx, height: canvasHeightPx, unit: 'px' },
          additionalInfo: {
            widthInches: canvasWidthPx / 72,
            heightInches: canvasHeightPx / 72,
            actualDPI,
            actualPixelWidth: widthPx,
            actualPixelHeight: heightPx,
          },
        },
      };

      onClose();
      window.dispatchEvent(new CustomEvent('navigate-to-print', {
        detail: { downloadData: window.pendingBrowserDownload },
      }));
    } catch (error) {
      toast.showError(t('save.toast.printPrepError', { error: error.message }));
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cce-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cce-modal">
        <h2 className="cce-modal-title">{t('save.title')}</h2>
        <p className="cce-modal-subtitle">{t('save.subtitle')}</p>

        <div className="cce-modal-content">
          <div className="cce-modal-thumbnail">
            {thumbnail ? (
              <img src={thumbnail} alt={t('save.previewAlt')} />
            ) : (
              <span>{t('save.loading')}</span>
            )}
          </div>
          <div className="cce-modal-details">
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              className="cce-modal-input"
              placeholder={t('save.namePlaceholder')}
            />
            <div className="cce-modal-info">{t('save.fromEditor')}</div>
            <label className="cce-modal-dpi">
              {t('save.printQualityLabel')}
              <select value={String(printDPI)} onChange={(e) => setPrintDPI(parseInt(e.target.value, 10))}>
                <option value="150">{t('save.dpiStandard')}</option>
                <option value="300">{t('save.dpiBest')}</option>
                <option value="600">Maximum (600 DPI)</option>
              </select>
            </label>
          </div>
        </div>

        <div className="cce-modal-actions">
          <button onClick={handleSaveToLibrary} disabled={isExporting} className="cce-btn cce-btn-success">
            {isExporting ? t('save.saving') : t('common:actions.save')}
          </button>
          <button onClick={handleOpenInPrint} disabled={isExporting} className="cce-btn cce-btn-primary">
            {isExporting ? t('save.preparing') : t('save.print')}
          </button>
          <button onClick={onClose} disabled={isExporting} className="cce-btn cce-btn-secondary">
            {t('common:actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Panel icons — lucide set (replaced the repurposed Polotno PNGs, where
// Draw showed an "Upload" icon and QR Code showed "Resize")
// Smart Objects icon: same make() pattern as toolbarIcons.jsx, defined
// locally to keep this feature's changes out of the shared icon module.
const IconPanelSmart = (props) => <Wand2 size={16} strokeWidth={2} aria-hidden="true" {...props} />;

const toolIconMapping = {
  [TOOLS.TEMPLATES]: IconPanelTemplates,
  [TOOLS.ELEMENTS]: IconPanelElements,
  [TOOLS.TEXT]: IconPanelText,
  [TOOLS.MEDIA]: IconPanelMedia,
  [TOOLS.SMART_OBJECTS]: IconPanelSmart,
  [TOOLS.LAYERS]: IconPanelLayers,
  [TOOLS.DRAW]: IconPanelDraw,
  [TOOLS.QRCODE]: IconPanelQr,
  [TOOLS.SETTINGS]: IconPanelSettings,
};

// Sidebar Component with Feature Gating
const Sidebar = ({ activeTool, onChangeTool }) => {
  const { t } = useTranslation('editor');
  const { hasFeature } = useFeature();
  const { showUpgradePrompt } = useUpgradePrompt();

  const tools = [
    { id: TOOLS.TEMPLATES, label: t('sidebar.templates') }, // Starter templates
    { id: TOOLS.ELEMENTS, label: t('sidebar.elements') },   // Shapes | Icons
    { id: TOOLS.TEXT, label: t('sidebar.text') },
    { id: TOOLS.MEDIA, label: t('sidebar.media') },         // Photos | Illustrations
    { id: TOOLS.SMART_OBJECTS, label: 'Smart' }, // Parametric smart objects
    { id: TOOLS.LAYERS, label: t('sidebar.layers') },
    { id: TOOLS.DRAW, label: t('sidebar.draw') },
    { id: TOOLS.QRCODE, label: t('sidebar.qrCode') },
    { id: TOOLS.SETTINGS, label: t('sidebar.settings') },
  ];

  const handleToolClick = (tool) => {
    const featureKey = TOOL_FEATURE_MAP[tool.id];
    // Check if this tool is gated
    if (featureKey && !hasFeature('print_editor', featureKey)) {
      // Show upgrade prompt instead of opening the tool
      showUpgradePrompt(tool.label, 'print_editor', featureKey);
      return;
    }
    onChangeTool(tool.id);
  };

  return (
    <aside className="cce-sidebar" data-tour="editor-tools">
      {tools.map((tool) => {
        const featureKey = TOOL_FEATURE_MAP[tool.id];
        const isLocked = featureKey && !hasFeature('print_editor', featureKey);
        const IconComp = toolIconMapping[tool.id];

        return (
          <button
            key={tool.id}
            className={`cce-sidebar-item ${activeTool === tool.id ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
            onClick={() => handleToolClick(tool)}
            title={isLocked ? t('sidebar.lockedTitle', { tool: tool.label }) : tool.label}
          >
            <span className="cce-sidebar-icon">
              {IconComp ? <IconComp size={22} /> : tool.label.charAt(0)}
            </span>
            <span className="cce-sidebar-label">{tool.label}</span>
            {isLocked && <span className="cce-sidebar-lock">🔒</span>}
          </button>
        );
      })}
    </aside>
  );
};

// Shape Icon Component - SVG shapes for the panel
const ShapeIcon = ({ type }) => {
  const svgProps = { width: 40, height: 40, viewBox: "0 0 40 40", fill: "#94a3b8", stroke: "none" };

  switch (type) {
    case 'rectangle':
      return <svg {...svgProps}><rect x="4" y="8" width="32" height="24" rx="0" /></svg>;
    case 'softRect':
      return <svg {...svgProps}><rect x="4" y="8" width="32" height="24" rx="6" /></svg>;
    case 'circle':
      return <svg {...svgProps}><circle cx="20" cy="20" r="16" /></svg>;
    case 'ellipse':
      return <svg {...svgProps}><ellipse cx="20" cy="20" rx="18" ry="12" /></svg>;
    case 'triangle':
      return <svg {...svgProps}><polygon points="20,4 36,36 4,36" /></svg>;
    case 'invTriangle':
      return <svg {...svgProps}><polygon points="4,4 36,4 20,36" /></svg>;
    case 'diamond':
      return <svg {...svgProps}><polygon points="20,2 38,20 20,38 2,20" /></svg>;
    case 'parallelogram':
      return <svg {...svgProps}><polygon points="10,8 38,8 30,32 2,32" /></svg>;
    case 'trapezoid':
      return <svg {...svgProps}><polygon points="8,8 32,8 38,32 2,32" /></svg>;
    case 'line':
      return <svg {...svgProps} stroke="#94a3b8" strokeWidth="3" fill="none"><line x1="4" y1="36" x2="36" y2="4" /></svg>;
    case 'arrow':
      return <svg {...svgProps} stroke="#94a3b8" strokeWidth="3" fill="none"><line x1="4" y1="20" x2="32" y2="20" /><polyline points="24,12 32,20 24,28" /></svg>;
    case 'doubleArrow':
      return <svg {...svgProps} stroke="#94a3b8" strokeWidth="3" fill="none"><line x1="10" y1="20" x2="30" y2="20" /><polyline points="22,12 30,20 22,28" /><polyline points="18,12 10,20 18,28" /></svg>;
    case 'pentagon':
      return <svg {...svgProps}><polygon points="20,2 38,14 32,36 8,36 2,14" /></svg>;
    case 'hexagon':
      return <svg {...svgProps}><polygon points="10,4 30,4 38,20 30,36 10,36 2,20" /></svg>;
    case 'octagon':
      return <svg {...svgProps}><polygon points="12,2 28,2 38,12 38,28 28,38 12,38 2,28 2,12" /></svg>;
    case 'star4':
      return <svg {...svgProps}><polygon points="20,2 24,16 38,20 24,24 20,38 16,24 2,20 16,16" /></svg>;
    case 'star':
      return <svg {...svgProps}><polygon points="20,2 24,14 38,14 27,23 31,36 20,28 9,36 13,23 2,14 16,14" /></svg>;
    case 'star6':
      return <svg {...svgProps}><polygon points="20,2 25,14 38,14 28,20 38,26 25,26 20,38 15,26 2,26 12,20 2,14 15,14" /></svg>;
    case 'star8':
      return <svg {...svgProps}><polygon points="20,2 23,12 30,5 27,15 38,14 29,20 38,26 27,25 30,35 23,28 20,38 17,28 10,35 13,25 2,26 11,20 2,14 13,15 10,5 17,12" /></svg>;
    case 'heart':
      return <svg {...svgProps}><path d="M20,36 C10,28 2,20 2,12 C2,6 8,2 14,2 C17,2 19,4 20,6 C21,4 23,2 26,2 C32,2 38,6 38,12 C38,20 30,28 20,36 Z" /></svg>;
    case 'cross':
      return <svg {...svgProps}><polygon points="14,2 26,2 26,14 38,14 38,26 26,26 26,38 14,38 14,26 2,26 2,14 14,14" /></svg>;
    case 'checkmark':
      return <svg {...svgProps}><path d="M6,22 L14,30 L34,10 L30,6 L14,22 L10,18 Z" /></svg>;
    case 'xmark':
      return <svg {...svgProps}><path d="M10,6 L20,16 L30,6 L34,10 L24,20 L34,30 L30,34 L20,24 L10,34 L6,30 L16,20 L6,10 Z" /></svg>;
    case 'cloud':
      return <svg {...svgProps}><path d="M10,30 C4,30 2,26 4,22 C2,18 4,14 10,14 C12,10 18,10 22,12 C24,8 32,10 32,16 C36,16 38,20 36,24 C38,28 34,30 30,30 Z" /></svg>;
    case 'lightning':
      return <svg {...svgProps}><polygon points="24,2 14,18 20,18 12,38 28,16 22,16 28,2" /></svg>;
    case 'crescent':
      return <svg {...svgProps}><path d="M20,2 C32,2 38,12 38,22 C38,32 28,38 18,38 C24,34 26,26 26,20 C26,14 24,6 18,2 Z" /></svg>;
    case 'speechBubble':
      return <svg {...svgProps}><path d="M4,6 h28 a4,4 0 0 1 4,4 v16 a4,4 0 0 1 -4,4 h-18 l-6,8 v-8 h-4 a4,4 0 0 1 -4,-4 v-16 a4,4 0 0 1 4,-4 z" /></svg>;
    case 'callout':
      return <svg {...svgProps}><path d="M2,4 h36 v24 h-20 l-8,10 v-10 h-8 z" /></svg>;
    case 'ribbon':
      return <svg {...svgProps}><path d="M2,12 L8,12 L8,6 L32,6 L32,12 L38,12 L32,18 L32,26 L8,26 L8,18 Z" /></svg>;
    default:
      return <svg {...svgProps}><rect x="4" y="4" width="32" height="32" /></svg>;
  }
};

// Tool Sidebars
// TextSidebar was replaced by TextInsertPanel
// (inspector/text/TextInsertPanel.jsx) — the owner's sectioned Add-text /
// Decorative / 3D drawer (2026-07-22). The old tabbed sidebar (Basic/
// Decorative/Special tabs, size cards, QUICK ADD row, legacy TEXT EFFECTS
// chips) is gone; those effect looks live on as real manifest styles in the
// Decorative grid, and TEXT_FX_STYLES / the getRecipe import went with them.

// Photos Tab Content - Pexels search (via wallet worker proxy) + upload
const PhotosTabContent = ({ editor }) => {
  const { t } = useTranslation('editor');
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('search'); // 'search' or 'upload'
  const [isDragging, setIsDragging] = useState(false);
  const [orientation, setOrientation] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const lastTermRef = useRef('');
  const { user } = useAuth();

  const PER_PAGE = 20;

  const searchImages = async (query, { append = false, orient } = {}) => {
    const useOrientation = orient ?? orientation; // Brightivity: a just-picked orientation, not the stale state
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim()) return;
    if (!user?.token) {
      toast.showError(t('photos.signInRequired'));
      return;
    }

    const nextPage = append ? page + 1 : 1;
    lastTermRef.current = searchTerm;
    setIsLoading(true);
    try {
      let url = `${PEXELS_PROXY_URL}?q=${encodeURIComponent(searchTerm)}&per_page=${PER_PAGE}&page=${nextPage}`;
      if (useOrientation !== 'all') url += `&orientation=${useOrientation}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const photos = data.photos || [];
      setImages((prev) => (append ? [...prev, ...photos] : photos));
      setPage(nextPage);
      setHasMore(photos.length === PER_PAGE);
    } catch (error) {
      console.error('Error searching images:', error);
      if (!append) setImages([]);
      toast.showError(t('photos.searchFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = () => {
    if (!isLoading && hasMore && lastTermRef.current) {
      searchImages(lastTermRef.current, { append: true });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchImages();
    }
  };

  const addImageToCanvas = (photo) => {
    // 'large' is ~940px wide — roughly 40 DPI on a 24" poster. Insert the
    // original at print grade; the hook downscales anything huge.
    editor?.addImageCapped(photo.src.original || photo.src.large2x || photo.src.large);
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      importFilesToEditor(files, editor, { toast });
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      importFilesToEditor(files, editor, { toast });
    }
  };

  return (
    <>
      {/* Sub-tabs for Photos */}
      <div className="cce-images-tabs">
        <button
          className={`cce-images-tab ${activeSubTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('search')}
        >
          {t('photos.tabSearch')}
        </button>
        <button
          className={`cce-images-tab ${activeSubTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('upload')}
        >
          {t('photos.tabUpload')}
        </button>
      </div>

      {activeSubTab === 'search' ? (
        <div className="cce-images-search">
          <div className="cce-search-box">
            <input
              type="text"
              placeholder={t('photos.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="cce-search-input"
            />
            <button
              onClick={() => searchImages()}
              className="cce-search-btn"
              disabled={isLoading}
            >
              {isLoading ? '...' : '🔍'}
            </button>
          </div>

          <div className="cce-quick-tags">
            {[t('photos.tags.nature'), t('photos.tags.business'), t('photos.tags.technology'), t('photos.tags.food'), t('photos.tags.people'), t('photos.tags.architecture'), t('photos.tags.animals'), t('photos.tags.travel')].map(tag => (
              <button
                key={tag}
                className="cce-quick-tag"
                onClick={() => {
                  setSearchQuery(tag);
                  searchImages(tag);
                }}
                disabled={isLoading}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="cce-images-orientation">
            <label>{t('photos.orientation')}</label>
            <select
              value={orientation}
              onChange={(e) => {
                setOrientation(e.target.value);
                if (lastTermRef.current) searchImages(lastTermRef.current, { orient: e.target.value });
              }}
            >
              <option value="all">{t('photos.orientationAny')}</option>
              <option value="portrait">{t('photos.orientationPortrait')}</option>
              <option value="landscape">{t('photos.orientationLandscape')}</option>
              <option value="square">{t('photos.orientationSquare')}</option>
            </select>
          </div>

          <div className="cce-images-grid">
            {isLoading ? (
              <div className="cce-images-empty">{t('photos.searching')}</div>
            ) : (
              images.map((photo) => (
                <div
                  key={photo.id}
                  className="cce-image-item"
                  onClick={() => addImageToCanvas(photo)}
                  title={t('photos.photoBy', { name: photo.photographer })}
                >
                  <img
                    src={photo.src.small}
                    alt={photo.alt || t('photos.pexelsPhotoAlt')}
                    onError={(e) => { e.target.style.opacity = '0.3'; }}
                  />
                  <div className="cce-image-credit">
                    📷 {photo.photographer}
                  </div>
                </div>
              ))
            )}
          </div>

          {images.length > 0 && hasMore && (
            <button
              type="button"
              className="cce-load-more-btn"
              onClick={loadMore}
              disabled={isLoading}
            >
              {isLoading ? t('photos.loadingMore') : t('photos.loadMore')}
            </button>
          )}

          {images.length === 0 && !isLoading && (
            <div className="cce-images-empty">
              {t('photos.emptyState')}
            </div>
          )}

          <div className="cce-pexels-credit">
            {t('photos.providedBy')} <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer">Pexels</a>
          </div>
        </div>
      ) : (
        <div className="cce-images-options">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,.pdf,application/pdf"
            multiple
            style={{ display: 'none' }}
          />

          <div
            ref={dropZoneRef}
            className={`cce-drop-zone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="cce-drop-zone-icon">
              {isDragging ? '📥' : '🖼️'}
            </div>
            <div className="cce-drop-zone-text">
              {isDragging ? t('photos.dropHere') : t('photos.dragDrop')}
            </div>
            <div className="cce-drop-zone-hint">
              {t('photos.clickToBrowse')}
            </div>
          </div>

          <p className="cce-upload-hint">
            {t('photos.uploadHint')}
          </p>
        </div>
      )}
    </>
  );
};

// Media Sidebar — Photos only. (The "Illustrations — Coming soon" stub was
// removed from shipped UI 2026-07-09; the tab returns when a real source
// lands.)
const MediaSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  if (!isOpen) return null;

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('media.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <PhotosTabContent editor={editor} />
    </div>
  );
};

// Icons Tab Content - Iconify API search
const IconsTabContent = ({ editor }) => {
  const { t } = useTranslation('editor');
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [icons, setIcons] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');

  const searchIcons = async (query) => {
    const searchTerm = query || searchQuery;
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_BASE}/design/icons/search?query=${encodeURIComponent(searchTerm)}&limit=48`
      );
      const data = await response.json();
      setIcons(data.icons || []);
    } catch (error) {
      console.error('Error searching icons:', error);
      setIcons([]);
      toast.showError(t('icons.searchFailed'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchIcons();
    }
  };

  const addIconToCanvas = (iconName) => {
    // Use the new addIcon function which stores metadata for color changing
    editor?.addIcon(iconName, selectedColor);
  };

  return (
    <div className="cce-icons-content">
      <div className="cce-search-box">
        <input
          type="text"
          placeholder={t('icons.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          className="cce-search-input"
        />
        <button
          onClick={() => searchIcons()}
          className="cce-search-btn"
          disabled={isSearching}
        >
          {isSearching ? '...' : '🔍'}
        </button>
      </div>

      <div className="cce-icon-color">
        <label>{t('icons.colorLabel')}</label>
        <input
          type="color"
          value={selectedColor}
          onChange={(e) => setSelectedColor(e.target.value)}
        />
      </div>

      <div className="cce-quick-tags">
        {['home', 'star', 'heart', 'arrow', 'check', 'user', 'settings', 'search'].map(tag => (
          <button
            key={tag}
            className="cce-quick-tag"
            onClick={() => {
              setSearchQuery(tag);
              searchIcons(tag);
            }}
            disabled={isSearching}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="cce-icons-grid">
        {icons.map((iconName, index) => (
          <button
            key={`${iconName}-${index}`}
            className="cce-icon-btn"
            onClick={() => addIconToCanvas(iconName)}
            title={iconName}
          >
            <img
              src={`${API_BASE}/design/icons/svg/${iconName.replace(':', '/')}.svg?color=${encodeURIComponent(selectedColor)}`}
              alt={iconName}
              style={{ width: 24, height: 24 }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </button>
        ))}
      </div>

      {icons.length === 0 && !isSearching && (
        <div className="cce-icons-empty">
          {t('icons.emptyState')}
        </div>
      )}

      <div className="cce-iconify-credit">
        {t('icons.iconsBy')} <a href="https://iconify.design" target="_blank" rel="noopener noreferrer">Iconify</a>
      </div>
    </div>
  );
};

// Elements Sidebar - Renamed from IconsSidebar, now with tabs
// Shapes Tab Content - moved from ShapesSidebar
const ShapesTabContent = ({ editor }) => {
  const { t } = useTranslation('editor');
  const basicShapes = [
    { id: 'rectangle', label: t('shapes.rectangle'), action: () => editor?.addRectangle() },
    { id: 'softRect', label: t('shapes.rounded'), action: () => editor?.addSoftRectangle() },
    { id: 'circle', label: t('shapes.circle'), action: () => editor?.addCircle() },
    { id: 'ellipse', label: t('shapes.ellipse'), action: () => editor?.addEllipse() },
    { id: 'triangle', label: t('shapes.triangle'), action: () => editor?.addTriangle() },
    { id: 'invTriangle', label: t('shapes.invTriangle'), action: () => editor?.addInverseTriangle() },
    { id: 'diamond', label: t('shapes.diamond'), action: () => editor?.addDiamond() },
    { id: 'parallelogram', label: t('shapes.parallelogram'), action: () => editor?.addParallelogram() },
    { id: 'trapezoid', label: t('shapes.trapezoid'), action: () => editor?.addTrapezoid() },
  ];

  const lineShapes = [
    { id: 'line', label: t('shapes.line'), action: () => editor?.addLine() },
    { id: 'arrow', label: t('shapes.arrow'), action: () => editor?.addArrow() },
    { id: 'doubleArrow', label: t('shapes.doubleArrow'), action: () => editor?.addDoubleArrow() },
  ];

  const polygonShapes = [
    { id: 'pentagon', label: t('shapes.pentagon'), action: () => editor?.addPentagon() },
    { id: 'hexagon', label: t('shapes.hexagon'), action: () => editor?.addHexagon() },
    { id: 'octagon', label: t('shapes.octagon'), action: () => editor?.addOctagon() },
  ];

  const starShapes = [
    { id: 'star4', label: t('shapes.star4'), action: () => editor?.addStar4() },
    { id: 'star', label: t('shapes.star5'), action: () => editor?.addStar() },
    { id: 'star6', label: t('shapes.star6'), action: () => editor?.addStar6() },
    { id: 'star8', label: t('shapes.star8'), action: () => editor?.addStar8() },
  ];

  const specialShapes = [
    { id: 'heart', label: t('shapes.heart'), action: () => editor?.addHeart() },
    { id: 'cross', label: t('shapes.cross'), action: () => editor?.addCross() },
    { id: 'checkmark', label: t('shapes.check'), action: () => editor?.addCheckmark() },
    { id: 'xmark', label: t('shapes.xMark'), action: () => editor?.addXMark() },
    { id: 'cloud', label: t('shapes.cloud'), action: () => editor?.addCloud() },
    { id: 'lightning', label: t('shapes.lightning'), action: () => editor?.addLightning() },
    { id: 'crescent', label: t('shapes.crescent'), action: () => editor?.addCrescent() },
    { id: 'speechBubble', label: t('shapes.speech'), action: () => editor?.addSpeechBubble() },
    { id: 'callout', label: t('shapes.callout'), action: () => editor?.addCallout() },
    { id: 'ribbon', label: t('shapes.ribbon'), action: () => editor?.addRibbon() },
  ];

  const renderShapeButtons = (shapes) => (
    <div className="cce-shapes-icon-grid">
      {shapes.map((shape) => (
        <button
          key={shape.id}
          className="cce-shape-icon-btn"
          onClick={shape.action}
          title={shape.label}
        >
          <ShapeIcon type={shape.id} />
        </button>
      ))}
    </div>
  );

  return (
    <div className="cce-shapes-content">
      <div className="cce-shapes-section">
        <h4 className="cce-shapes-category">{t('shapes.categories.basic')}</h4>
        {renderShapeButtons(basicShapes)}
      </div>

      <div className="cce-shapes-section">
        <h4 className="cce-shapes-category">{t('shapes.categories.linesArrows')}</h4>
        {renderShapeButtons(lineShapes)}
      </div>

      <div className="cce-shapes-section">
        <h4 className="cce-shapes-category">{t('shapes.categories.polygons')}</h4>
        {renderShapeButtons(polygonShapes)}
      </div>

      <div className="cce-shapes-section">
        <h4 className="cce-shapes-category">{t('shapes.categories.stars')}</h4>
        {renderShapeButtons(starShapes)}
      </div>

      <div className="cce-shapes-section">
        <h4 className="cce-shapes-category">{t('shapes.categories.special')}</h4>
        {renderShapeButtons(specialShapes)}
      </div>
    </div>
  );
};

const ElementsSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const [activeTab, setActiveTab] = useState('shapes');

  // Stickers tab removed 2026-07-10 (owner skipped the Tenor key — a
  // permanently-erroring tab must not ship; /wallet/tenor-search stays
  // deployed if stickers ever return).
  const tabs = [
    { id: 'shapes', label: t('elements.tabShapes') },
    { id: 'icons', label: t('elements.tabIcons') },
  ];

  if (!isOpen) return null;

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('elements.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <PanelTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'shapes' && <ShapesTabContent editor={editor} />}
      {activeTab === 'icons' && <IconsTabContent editor={editor} />}
    </div>
  );
};

// Draw Sidebar - Drawing tools panel
const DrawSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const [brushWidth, setBrushWidth] = useState(5);
  const [brushColor, setBrushColor] = useState('#000000');
  const [brushType, setBrushType] = useState('pencil');
  const [strokeStyle, setStrokeStyle] = useState('solid'); // solid, dashed, dotted
  const [isEraser, setIsEraser] = useState(false);

  // Update brush settings when they change
  useEffect(() => {
    if (!editor || !isOpen) return;

    // Set brush type
    editor.setBrushType(brushType);

    // Set brush width
    editor.setDrawingBrushWidth(brushWidth);

    // Set brush color (or white for eraser)
    if (isEraser) {
      editor.setDrawingBrushColor('#ffffff');
    } else {
      editor.setDrawingBrushColor(brushColor);
    }
  }, [editor, isOpen, brushWidth, brushColor, brushType, isEraser]);

  if (!isOpen) return null;

  const brushTypes = [
    { id: 'pencil', label: t('draw.brushPencil'), icon: '✏️' },
    { id: 'spray', label: t('draw.brushSpray'), icon: '💨' },
    { id: 'circle', label: t('draw.brushCircle'), icon: '⭕' },
  ];

  const presetColors = [
    '#000000', '#ffffff', '#ef4444', '#f97316', '#eab308',
    '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280',
  ];

  const presetWidths = [2, 5, 10, 20, 40];

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('draw.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-draw-content">
        {/* Eraser Toggle */}
        <div className="cce-draw-section">
          <div className="cce-eraser-toggle">
            <button
              className={`cce-eraser-btn ${!isEraser ? 'active' : ''}`}
              onClick={() => setIsEraser(false)}
            >
              ✏️ {t('draw.modeDraw')}
            </button>
            <button
              className={`cce-eraser-btn ${isEraser ? 'active' : ''}`}
              onClick={() => setIsEraser(true)}
            >
              🧹 {t('draw.modeEraser')}
            </button>
          </div>
        </div>

        {/* Brush Type */}
        <div className="cce-draw-section">
          <h4>{t('draw.brushType')}</h4>
          <div className="cce-brush-types">
            {brushTypes.map((brush) => (
              <button
                key={brush.id}
                className={`cce-brush-type-btn ${brushType === brush.id ? 'active' : ''}`}
                onClick={() => setBrushType(brush.id)}
                title={brush.label}
              >
                <span className="cce-brush-icon">{brush.icon}</span>
                <span className="cce-brush-label">{brush.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Brush Width */}
        <div className="cce-draw-section">
          <h4>{t('draw.brushSize', { size: brushWidth })}</h4>
          <input
            type="range"
            min={1}
            max={100}
            value={brushWidth}
            onChange={(e) => setBrushWidth(parseInt(e.target.value, 10))}
            className="cce-brush-slider"
          />
          <div className="cce-preset-widths">
            {presetWidths.map((w) => (
              <button
                key={w}
                className={`cce-width-btn ${brushWidth === w ? 'active' : ''}`}
                onClick={() => setBrushWidth(w)}
              >
                <span
                  className="cce-width-preview"
                  style={{
                    width: Math.min(w, 30),
                    height: Math.min(w, 30),
                    backgroundColor: brushColor,
                  }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Brush Color */}
        {!isEraser && (
          <div className="cce-draw-section">
            <h4>{t('draw.brushColor')}</h4>
            <div className="cce-draw-color-picker">
              <input
                type="color"
                value={brushColor}
                onChange={(e) => setBrushColor(e.target.value)}
                className="cce-color-input"
              />
              <span className="cce-color-hex">{brushColor}</span>
            </div>
            <div className="cce-preset-colors">
              {presetColors.map((color) => (
                <button
                  key={color}
                  className={`cce-color-preset ${brushColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setBrushColor(color)}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        {/* Drawing Tips */}
        <div className="cce-draw-tips">
          <p>{t('draw.tipDrag')}</p>
          <p>{t('draw.tipExit')}</p>
        </div>
      </div>
    </div>
  );
};

// Layers Sidebar - Layer management panel
const LayersSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const [layers, setLayers] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [expandedLayer, setExpandedLayer] = useState(null);

  // Generate thumbnail for a layer object
  const generateThumbnail = useCallback((obj) => {
    if (!obj) return null;
    try {
      // Clone to avoid modifying original
      const clone = obj.toObject();
      const size = 40;
      const scale = Math.min(size / obj.width, size / obj.height, 1);
      return obj.toDataURL({
        format: 'png',
        quality: 0.5,
        multiplier: scale,
      });
    } catch (e) {
      return null;
    }
  }, []);

  // Refresh layers when panel opens or editor changes
  useEffect(() => {
    if (isOpen && editor) {
      const newLayers = editor.getLayers();
      setLayers(newLayers);

      // Generate thumbnails for each layer
      const newThumbnails = {};
      newLayers.forEach((layer) => {
        if (layer.object) {
          const thumb = generateThumbnail(layer.object);
          if (thumb) newThumbnails[layer.id] = thumb;
        }
      });
      setThumbnails(newThumbnails);
    }
  }, [isOpen, editor, editor?.selectedObjects, generateThumbnail]);

  // Refresh on canvas changes
  useEffect(() => {
    if (!isOpen || !editor?.canvas) return;

    const refreshLayers = () => setLayers(editor.getLayers());

    editor.canvas.on('object:added', refreshLayers);
    editor.canvas.on('object:removed', refreshLayers);
    editor.canvas.on('object:modified', refreshLayers);

    return () => {
      editor.canvas.off('object:added', refreshLayers);
      editor.canvas.off('object:removed', refreshLayers);
      editor.canvas.off('object:modified', refreshLayers);
    };
  }, [isOpen, editor]);

  const getLayerIcon = (type) => {
    switch (type) {
      case 'textbox':
      case 'text':
      case 'i-text':
      case 'itext': // v6 live-object casing
        return 'T';
      case 'image':
        return '🖼';
      case 'rect':
        return '▭';
      case 'circle':
        return '○';
      case 'triangle':
        return '△';
      case 'polygon':
        return '⬡';
      case 'path':
        return '✦';
      case 'line':
        return '─';
      default:
        return '□';
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const draggedLayer = layers[draggedIndex];
    const targetLayer = layers[dropIndex];

    // Move in canvas (reversed because layers are displayed top-to-bottom)
    if (draggedIndex < dropIndex) {
      // Moving down in the list = moving backward in canvas
      let current = draggedLayer.object;
      for (let i = draggedIndex; i < dropIndex; i++) {
        editor?.canvas.sendObjectBackwards(current);
      }
    } else {
      // Moving up in the list = moving forward in canvas
      let current = draggedLayer.object;
      for (let i = draggedIndex; i > dropIndex; i--) {
        editor?.canvas.bringObjectForward(current);
      }
    }

    // Keep workspace at back
    const workspace = editor?.canvas.getObjects().find((obj) => obj.name === "clip");
    if (workspace) editor?.canvas.sendObjectToBack(workspace); // v6: object-level sendToBack removed

    editor?.canvas.renderAll();
    setLayers(editor?.getLayers() || []);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (!isOpen) return null;

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('layers.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-layers-content">
        {layers.length === 0 ? (
          <div className="cce-layers-empty">
            {t('layers.emptyLine1')}<br />
            {t('layers.emptyLine2')}
          </div>
        ) : (
          <div className="cce-layers-list">
            {layers.map((layer, index) => {
              const isSelected = editor?.selectedObjects?.includes(layer.object);
              const isExpanded = expandedLayer === layer.id;
              const thumbnail = thumbnails[layer.id];

              return (
                <div key={layer.id} className="cce-layer-wrapper">
                  <div
                    className={`cce-layer-item ${isSelected ? 'selected' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => layer.locked ? null : editor?.selectLayer(layer.object)}
                  >
                    {/* Drag Handle */}
                    <span className="cce-layer-drag">⋮⋮</span>

                    {/* Thumbnail Preview */}
                    <div className="cce-layer-thumbnail">
                      {thumbnail ? (
                        <img src={thumbnail} alt={layer.name} />
                      ) : (
                        <span className="cce-layer-icon">{getLayerIcon(layer.type)}</span>
                      )}
                    </div>

                    {/* Layer Name */}
                    <span className="cce-layer-name">{layer.name}</span>

                    {/* Expand/Collapse for opacity */}
                    <button
                      className={`cce-layer-action cce-layer-expand ${isExpanded ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedLayer(isExpanded ? null : layer.id);
                      }}
                      title={t('layers.opacity')}
                    >
                      ◐
                    </button>

                    {/* Layer Actions */}
                    <div className="cce-layer-actions">
                      {/* Visibility Toggle */}
                      <button
                        className={`cce-layer-action ${!layer.visible ? 'off' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          editor?.toggleLayerVisibility(layer.object);
                          setLayers(editor?.getLayers() || []);
                        }}
                        title={layer.visible ? t('layers.hide') : t('layers.show')}
                      >
                        {layer.visible ? '👁' : '👁‍🗨'}
                      </button>

                      {/* Lock Toggle */}
                      <button
                        className={`cce-layer-action ${layer.locked ? 'on' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          editor?.toggleLayerLock(layer.object);
                          setLayers(editor?.getLayers() || []);
                        }}
                        title={layer.locked ? t('layers.unlock') : t('layers.lock')}
                      >
                        {layer.locked ? '🔒' : '🔓'}
                      </button>

                      {/* Delete */}
                      <button
                        className="cce-layer-action cce-layer-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          editor?.deleteLayer(layer.object);
                        }}
                        title={t('common:actions.delete')}
                      >
                        🗑
                      </button>
                    </div>
                  </div>

                  {/* Expanded Opacity Slider */}
                  {isExpanded && (
                    <div className="cce-layer-opacity-row">
                      <label>{t('layers.opacity')}</label>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={layer.object?.opacity ?? 1}
                        onChange={(e) => {
                          editor?.changeLayerOpacity(layer.object, parseFloat(e.target.value));
                          setLayers(editor?.getLayers() || []);
                        }}
                        onPointerUp={() => editor?.commit?.()}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>{Math.round((layer.object?.opacity ?? 1) * 100)}%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Layer Tips */}
        <div className="cce-layers-tips">
          <p>{t('layers.tipReorder')}</p>
          <p>{t('layers.tipSelect')}</p>
        </div>
      </div>
    </div>
  );
};

// QR Code Sidebar Component — generates locally via the `qrcode` package
// (vector SVG onto the canvas: crisp at any print size, works offline, and
// saved designs no longer re-fetch a remote PNG on reload).
const QRCodeSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const toast = useToast();
  const [qrData, setQrData] = useState('');
  const [qrSize, setQrSize] = useState('medium'); // small, medium, large
  const [qrColor, setQrColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Placed width as a fraction of the canvas width (the QR itself is vector)
  const sizeFractionMap = {
    small: 0.15,
    medium: 0.25,
    large: 0.4,
  };

  const presetColors = [
    '#000000', '#ffffff', '#ef4444', '#f97316', '#eab308',
    '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280',
  ];

  if (!isOpen) return null;

  const generateQR = async () => {
    if (!qrData.trim()) return;

    setIsLoading(true);
    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        margin: 1,
        width: 300,
        color: { dark: qrColor, light: bgColor },
      });
      setPreviewUrl(dataUrl);
    } catch (err) {
      console.error('QR generation failed:', err);
      toast.showError(t('qr.generateFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const addToCanvas = async () => {
    if (!qrData.trim() || !editor) return;
    try {
      const svg = await QRCode.toString(qrData, {
        type: 'svg',
        margin: 1,
        color: { dark: qrColor, light: bgColor },
      });
      const frac = sizeFractionMap[qrSize] || 0.25;
      editor.addSvgString(svg, {
        targetWidth: Math.round((editor.getWidth?.() || 1728) * frac),
      });
    } catch (err) {
      console.error('QR add failed:', err);
      toast.showError(t('qr.addFailed'));
    }
  };

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('qr.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-qr-content">
        <div className="cce-qr-section">
          <label className="cce-qr-label">{t('qr.dataLabel')}</label>
          <textarea
            className="cce-qr-input"
            placeholder={t('qr.dataPlaceholder')}
            value={qrData}
            onChange={(e) => setQrData(e.target.value)}
            rows={3}
          />
        </div>

        <div className="cce-qr-section">
          <label className="cce-qr-label">{t('qr.sizeLabel')}</label>
          <div className="cce-qr-size-options">
            {['small', 'medium', 'large'].map((size) => (
              <button
                key={size}
                className={`cce-qr-size-btn ${qrSize === size ? 'active' : ''}`}
                onClick={() => setQrSize(size)}
              >
                {{ small: t('qr.sizeSmall'), medium: t('qr.sizeMedium'), large: t('qr.sizeLarge') }[size]}
              </button>
            ))}
          </div>
        </div>

        <div className="cce-qr-section">
          <label className="cce-qr-label">{t('qr.colorLabel')}</label>
          <div className="cce-qr-color-row">
            <input
              type="color"
              value={qrColor}
              onChange={(e) => setQrColor(e.target.value)}
              className="cce-qr-color-picker"
            />
            <div className="cce-qr-preset-colors">
              {presetColors.slice(0, 5).map((color) => (
                <button
                  key={color}
                  className={`cce-qr-preset-color ${qrColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setQrColor(color)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="cce-qr-section">
          <label className="cce-qr-label">{t('qr.bgColorLabel')}</label>
          <div className="cce-qr-color-row">
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="cce-qr-color-picker"
            />
            <div className="cce-qr-preset-colors">
              {presetColors.slice(0, 5).map((color) => (
                <button
                  key={color}
                  className={`cce-qr-preset-color ${bgColor === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setBgColor(color)}
                />
              ))}
            </div>
          </div>
        </div>

        <button
          className="cce-qr-generate-btn"
          onClick={generateQR}
          disabled={!qrData.trim() || isLoading}
        >
          {isLoading ? t('qr.generating') : t('qr.generate')}
        </button>

        {previewUrl && (
          <div className="cce-qr-preview-section">
            <label className="cce-qr-label">{t('qr.preview')}</label>
            <div className="cce-qr-preview">
              <img src={previewUrl} alt={t('qr.previewAlt')} />
            </div>
            <button
              className="cce-qr-add-btn"
              onClick={addToCanvas}
            >
              {t('qr.addToCanvas')}
            </button>
          </div>
        )}

        <div className="cce-qr-tips">
          <p>{t('qr.tipsTitle')}</p>
          <ul>
            <li>{t('qr.tip1')}</li>
            <li>{t('qr.tip2')}</li>
            <li>{t('qr.tip3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Default canvas dimensions (24" x 36" at 72 DPI)
const TEMPLATE_WIDTH = 1728;
const TEMPLATE_HEIGHT = 2592;

// Helper to create the workspace clip object required by the canvas
const createClipObject = (fill = "#ffffff") => ({
  type: "rect",
  left: 0,
  top: 0,
  width: TEMPLATE_WIDTH,
  height: TEMPLATE_HEIGHT,
  fill: fill,
  name: "clip",
  selectable: false,
  hasControls: false,
  evented: false,
  shadow: {
    color: "rgba(0,0,0,0.8)",
    blur: 5,
    offsetX: 0,
    offsetY: 0
  }
});


// Templates Sidebar Component
const TemplatesSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const toast = useToast();
  const { confirm } = useConfirm();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isLoading, setIsLoading] = useState(false);
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Get unique categories from remote templates
  const categories = ['All', ...new Set(remoteTemplates.map(t => t.category))];

  const filteredTemplates = selectedCategory === 'All'
    ? remoteTemplates
    : remoteTemplates.filter(t => t.category === selectedCategory);

  // Fetch remote templates from API
  useEffect(() => {
    if (!isOpen) return;

    const fetchTemplates = async () => {
      // Only fetch if we haven't fetched yet
      if (remoteTemplates.length > 0 || isFetchingTemplates) return;

      setIsFetchingTemplates(true);
      setFetchError(null);

      try {
        const response = await fetch(`${TEMPLATES_API_URL}/templates/list?limit=200`);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const data = await response.json();

        // Transform API response to match template format
        // Use worker proxy URLs instead of direct S3 URLs (S3 is private)
        const templates = data.templates.map(t => ({
          id: t.id,
          name: t.name,
          category: t.category,
          style: t.style,
          thumbnail_url: `${TEMPLATES_API_URL}/templates/${t.id}/thumbnail`,
          json_url: `${TEMPLATES_API_URL}/templates/${t.id}/json`,
        }));

        setRemoteTemplates(templates);
      } catch (err) {
        console.error('Error fetching templates:', err);
        setFetchError(err.message);
      } finally {
        setIsFetchingTemplates(false);
      }
    };

    fetchTemplates();
  }, [isOpen]);

  const handleLoadTemplate = async (template) => {
    if (!editor) return;
    // Brightivity: a template replaces the whole poster — ask first when there is work on it (Undo also brings it back).
    const onPoster = (editor.canvas?.getObjects?.() || []).filter((o) => o.selectable !== false && !o.excludeFromExport).length;
    if (onPoster > 0 && !(await confirm(`Replace your poster with “${template.name}”? You can undo this.`, { title: 'Use this template?', confirmText: 'Use template', cancelText: 'Keep my poster' }))) return;

    setIsLoading(true);
    try {
      // Fetch JSON from remote URL
      const response = await fetch(template.json_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status}`);
      }
      const templateJson = await response.json();

      // Use the editor's loadJson method
      editor.loadJson(templateJson);
      toast.showSuccess(t('templates.loaded', { name: template.name }));
    } catch (error) {
      console.error('Error loading template:', error);
      toast.showError(t('templates.loadFailed', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('templates.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-templates-content">
        {/* Category filter */}
        <div className="cce-templates-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`cce-template-category-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'All' ? t('templates.categoryAll') : cat}
            </button>
          ))}
        </div>

        {/* Loading indicator for fetching templates */}
        {isFetchingTemplates && (
          <div className="cce-templates-loading">
            <span>{t('templates.loadingList')}</span>
          </div>
        )}

        {/* Templates grid */}
        <div className="cce-templates-grid">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="cce-template-card"
              onClick={() => handleLoadTemplate(template)}
            >
              <div className="cce-template-preview">
                <img
                  src={template.thumbnail_url}
                  alt={template.name}
                  className="cce-template-thumbnail-img"
                  loading="lazy"
                />
              </div>
              <span className="cce-template-name">{template.name}</span>
            </div>
          ))}
        </div>

        {/* Empty state */}
        {filteredTemplates.length === 0 && !isFetchingTemplates && (
          <div className="cce-templates-empty">
            <span>{fetchError ? t('templates.fetchError', { error: fetchError }) : t('templates.empty')}</span>
          </div>
        )}

        {isLoading && (
          <div className="cce-templates-loading">
            <span>{t('templates.loadingOne')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const SettingsSidebar = ({ editor, isOpen, onClose }) => {
  const { t } = useTranslation('editor');
  const DPI = 72; // Standard screen DPI
  const [widthInches, setWidthInches] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [background, setBackground] = useState('#ffffff');
  const [bgType, setBgType] = useState('solid'); // 'solid' or 'gradient'
  const [gradientColor1, setGradientColor1] = useState('#3b82f6');
  const [gradientColor2, setGradientColor2] = useState('#8b5cf6');
  const [gradientDirection, setGradientDirection] = useState('vertical');

  useEffect(() => {
    if (editor && isOpen) {
      // Convert pixels to inches
      setWidthInches((editor.getWidth() / DPI).toFixed(2));
      setHeightInches((editor.getHeight() / DPI).toFixed(2));
    }
  }, [editor, isOpen]);

  if (!isOpen) return null;

  const handleResize = () => {
    // Convert inches to pixels
    const wPx = Math.round(parseFloat(widthInches) * DPI);
    const hPx = Math.round(parseFloat(heightInches) * DPI);
    if (wPx > 0 && hPx > 0) {
      editor?.changeSize({ width: wPx, height: hPx });
    }
  };

  const handlePreset = (preset) => {
    setWidthInches(String(preset.width));
    setHeightInches(String(preset.height));
    editor?.changeSize({ width: preset.widthPx, height: preset.heightPx });
  };

  // commit=false for live color-wheel drags (history lands once on blur);
  // preset swatch clicks are discrete and commit immediately.
  const handleSolidBackground = (color, commit = true) => {
    setBackground(color);
    setBgType('solid');
    editor?.changeBackground(color);
    if (commit) editor?.commit?.();
  };

  const handleGradientBackground = () => {
    setBgType('gradient');
    editor?.changeBackgroundGradient(gradientColor1, gradientColor2, gradientDirection);
    editor?.commit?.();
  };

  return (
    <div className="cce-tool-sidebar">
      <div className="cce-tool-header">
        <h3>{t('settings.title')}</h3>
        <button onClick={onClose} className="cce-close-btn">×</button>
      </div>

      <div className="cce-settings-section">
        <h4>{t('settings.customSize')}</h4>
        <div className="cce-size-inputs">
          <div>
            <label>{t('settings.width')}</label>
            <input
              type="number"
              value={widthInches}
              onChange={(e) => setWidthInches(e.target.value)}
              step="0.5"
              min="1"
            />
            <span className="cce-unit">"</span>
          </div>
          <div>
            <label>{t('settings.height')}</label>
            <input
              type="number"
              value={heightInches}
              onChange={(e) => setHeightInches(e.target.value)}
              step="0.5"
              min="1"
            />
            <span className="cce-unit">"</span>
          </div>
        </div>
        <button className="cce-resize-btn" onClick={handleResize}>
          {t('settings.resize')}
        </button>
      </div>

      <div className="cce-settings-section">
        <h4>{t('settings.presets')}</h4>
        <div className="cce-presets">
          {SIZE_PRESETS.map((preset) => (
            <button
              key={preset.label}
              className="cce-preset-btn"
              onClick={() => handlePreset(preset)}
            >
              <span className="cce-preset-label">{t(`settings.presetLabel.${preset.kind}`, { w: preset.width, h: preset.height })}</span>
              <span className="cce-preset-size">{preset.width}" × {preset.height}"</span>
            </button>
          ))}
        </div>
      </div>

      <div className="cce-settings-section">
        <h4>{t('settings.background')}</h4>

        {/* Background Type Toggle */}
        <div className="cce-bg-type-toggle">
          <button
            className={`cce-bg-type-btn ${bgType === 'solid' ? 'active' : ''}`}
            onClick={() => setBgType('solid')}
          >
            {t('settings.solid')}
          </button>
          <button
            className={`cce-bg-type-btn ${bgType === 'gradient' ? 'active' : ''}`}
            onClick={() => setBgType('gradient')}
          >
            {t('settings.gradient')}
          </button>
        </div>

        {bgType === 'solid' ? (
          <>
            {/* Color Picker */}
            <div className="cce-bg-color-picker">
              <input
                type="color"
                value={background}
                onChange={(e) => handleSolidBackground(e.target.value, false)}
                onBlur={() => editor?.commit?.()}
                className="cce-bg-color-input"
              />
              <span className="cce-color-hex">{background}</span>
            </div>

            {/* Preset Colors */}
            <div className="cce-color-grid">
              {['#ffffff', '#000000', '#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1',
                '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6',
                '#ec4899', '#14b8a6', '#6366f1', '#a855f7'].map((color) => (
                <button
                  key={color}
                  className={`cce-color-btn ${background === color && bgType === 'solid' ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleSolidBackground(color)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Gradient Controls */}
            <div className="cce-gradient-bg-controls">
              <div className="cce-gradient-colors">
                <input
                  type="color"
                  value={gradientColor1}
                  onChange={(e) => setGradientColor1(e.target.value)}
                  title={t('settings.startColor')}
                />
                <span>→</span>
                <input
                  type="color"
                  value={gradientColor2}
                  onChange={(e) => setGradientColor2(e.target.value)}
                  title={t('settings.endColor')}
                />
              </div>
              <select
                value={gradientDirection}
                onChange={(e) => setGradientDirection(e.target.value)}
                className="cce-gradient-direction"
              >
                <option value="horizontal">{t('settings.gradientHorizontal')}</option>
                <option value="vertical">{t('settings.gradientVertical')}</option>
                <option value="diagonal">{t('settings.gradientDiagonal')}</option>
              </select>
              <button
                className="cce-gradient-apply-btn"
                onClick={handleGradientBackground}
              >
                {t('settings.applyGradient')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Footer Component — undo/redo, snap toggle, zoom readout + presets + Fit
const ZOOM_PRESETS = [25, 50, 75, 100, 150, 200, 300];

const Footer = ({ editor }) => {
  const { t } = useTranslation('editor');
  const [zoomPct, setZoomPct] = useState(100);
  const [snapOn, setSnapOn] = useState(true);

  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return;
    setSnapOn(editor?.isSnappingEnabled?.() ?? true);
    const update = () => setZoomPct(Math.round(canvas.getZoom() * 100));
    update();
    canvas.on('after:render', update);
    return () => canvas.off('after:render', update);
  }, [editor]);

  const handleZoomSelect = (e) => {
    const v = e.target.value;
    if (v === 'fit') editor?.zoomToFit();
    else if (v) editor?.setZoomPercent(parseInt(v, 10));
  };

  const toggleSnap = () => {
    const next = !snapOn;
    editor?.setSnapping?.(next);
    setSnapOn(next);
  };

  return (
    <div className="cce-footer">
      <div className="cce-footer-left">
        {/* Undo/Redo RETIRED here (global-undo lane, 2026-07-21): their one
            always-visible home is the ribbon's left history rail — rendered
            for every selection kind, which this footer pair merely twinned. */}
        <button
          onClick={toggleSnap}
          className={`cce-footer-btn${snapOn ? ' active' : ''}`}
          title={t('footer.snapTitle')}
        >
          <IconSnap size={14} /> {snapOn ? t('footer.snapOn') : t('footer.snapOff')}
        </button>
      </div>
      <div className="cce-footer-right">
        <button onClick={() => editor?.zoomOut()} className="cce-footer-btn" title={t('footer.zoomOutTitle', { shortcut: 'Ctrl+-' })} aria-label={t('footer.zoomOut')}>
          <IconZoomOut size={14} />
        </button>
        <select
          className="cce-footer-zoom"
          value={ZOOM_PRESETS.includes(zoomPct) ? String(zoomPct) : ''}
          onChange={handleZoomSelect}
          aria-label={t('footer.zoomLevel')}
        >
          {!ZOOM_PRESETS.includes(zoomPct) && (
            <option value="" disabled>{zoomPct}%</option>
          )}
          {ZOOM_PRESETS.map((z) => (
            <option key={z} value={String(z)}>{z}%</option>
          ))}
          <option value="fit">{t('footer.fit')}</option>
        </select>
        <button onClick={() => editor?.zoomIn()} className="cce-footer-btn" title={t('footer.zoomInTitle', { shortcut: 'Ctrl+=' })} aria-label={t('footer.zoomIn')}>
          <IconZoomIn size={14} />
        </button>
        <button onClick={() => editor?.zoomToFit()} className="cce-footer-btn" title={t('footer.fitTitle', { shortcut: 'Ctrl+0' })}>
          <IconFit size={14} /> {t('footer.fit')}
        </button>
      </div>
    </div>
  );
};

// Wrap the editor facade so the INSERT verbs also arm a "just inserted" latch
// (owner directive 2026-07-21: a left-panel add closes its drawer). Every add*
// verb funnels an object onto the canvas; `loadJson` is the template-apply add.
// Only these are intercepted — all other methods, getters and values (including
// editor.canvas) pass through unchanged, and the real verb keeps its own `this`.
const INSERT_VERB = (prop) =>
  typeof prop === 'string' && (prop.startsWith('add') || prop === 'loadJson');

function withInsertLatch(editor, arm) {
  return new Proxy(editor, {
    get(target, prop) {
      const value = target[prop];
      if (typeof value === 'function' && INSERT_VERB(prop)) {
        return (...args) => {
          arm();
          return value.apply(target, args);
        };
      }
      return value;
    },
  });
}

// Main Editor Component
export default function StudioCoreEditor({ initialDesign }) {
  const { t } = useTranslation('editor');
  const { hasFeature } = useFeature();
  const toast = useToast();

  // Surface image-load failures from the canvas hook (the hook has no toast
  // access; it dispatches this event instead of failing silently).
  useEffect(() => {
    const onImageLoadFailed = () => {
      toast.showError(t('editor.imageLoadFailed'));
    };
    const onFontLoadFailed = (e) => {
      toast.showError(t('editor.fontLoadFailed', { family: e.detail?.family || '' }));
    };
    // Brightivity: a texture that fails to load was silent (nothing listened).
    const onTextureLoadFailed = () => toast.showError(t('editor.imageLoadFailed'));
    window.addEventListener('cce-image-load-failed', onImageLoadFailed);
    window.addEventListener('cce-font-load-failed', onFontLoadFailed);
    window.addEventListener('cce-texture-load-failed', onTextureLoadFailed);
    return () => {
      window.removeEventListener('cce-image-load-failed', onImageLoadFailed);
      window.removeEventListener('cce-font-load-failed', onFontLoadFailed);
      window.removeEventListener('cce-texture-load-failed', onTextureLoadFailed);
    };
  }, [toast, t]);

  // Find the first available (unlocked) tool to use as default
  const getDefaultTool = () => {
    const toolOrder = [TOOLS.TEMPLATES, TOOLS.SETTINGS, TOOLS.ELEMENTS, TOOLS.TEXT, TOOLS.MEDIA, TOOLS.LAYERS, TOOLS.DRAW, TOOLS.QRCODE];
    for (const tool of toolOrder) {
      const featureKey = TOOL_FEATURE_MAP[tool];
      if (!featureKey || hasFeature('print_editor', featureKey)) {
        return tool;
      }
    }
    return TOOLS.SELECT; // All tools locked, show no panel
  };

  const [activeTool, setActiveTool] = useState(getDefaultTool);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  // .cce-container — positioning host for the floating drawer/inspector overlays
  // and the element the canvas-band CSS vars are written onto.
  const rootRef = useRef(null);

  // Auto-close-on-insert latch (owner directive 2026-07-21). An insert-y left
  // panel arms this the instant it calls an add-verb; the canvas 'object:added'
  // that follows (sync for shapes/text, async for media/icon/svg/smart-objects,
  // many for a template loadJson) is the object LANDING → close the drawer.
  // Correlating the panel action with the land is what lets initial-load /
  // undo / redo / recolor (which fire 'object:added' too, but through the REAL
  // editor, never arming this) leave the drawer untouched.
  const pendingInsertRef = useRef(false);
  const pendingInsertTimerRef = useRef(null);
  const armPendingInsert = useCallback(() => {
    pendingInsertRef.current = true;
    if (pendingInsertTimerRef.current) clearTimeout(pendingInsertTimerRef.current);
    // Safety: an insert whose object never lands (e.g. image load failure) must
    // not leave the latch armed to close on the next unrelated add.
    pendingInsertTimerRef.current = setTimeout(() => {
      pendingInsertRef.current = false;
      pendingInsertTimerRef.current = null;
    }, 6000);
  }, []);

  const onClearSelection = useCallback(() => {
    if (selectionDependentTools.includes(activeTool)) {
      setActiveTool(TOOLS.SELECT);
    }
  }, [activeTool]);

  // ---- Autosave draft (crash recovery) ----
  // Every history commit schedules a local draft write: 2 s after the last
  // edit, capped at 30 s from the first edit of a burst. Drafts live in their
  // own per-user slot (editorDraftSave IPC) — never in the library, never
  // synced to R2.
  const draftTimerRef = useRef(null);
  const draftMaxWaitRef = useRef(null);
  const latestDraftPayloadRef = useRef(null);
  const lastSavedJsonRef = useRef(null);
  const draftMetaRef = useRef({
    designName: initialDesign?.name || t('save.untitledName'),
    designId: initialDesign?.designId || null,
  });

  const writeDraft = useCallback(() => {
    const payload = latestDraftPayloadRef.current;
    if (!payload) return;
    // Canvas matches the last library save — nothing worth recovering.
    if (payload.json === lastSavedJsonRef.current) return;
    platform.editorDraftSave({
      json: payload.json,
      widthPx: payload.width,
      heightPx: payload.height,
      designName: draftMetaRef.current.designName,
      designId: draftMetaRef.current.designId,
      savedAt: new Date().toISOString(),
    }).catch(() => {});
  }, []);

  const flushDraft = useCallback(() => {
    if (draftTimerRef.current) { clearTimeout(draftTimerRef.current); draftTimerRef.current = null; }
    if (draftMaxWaitRef.current) { clearTimeout(draftMaxWaitRef.current); draftMaxWaitRef.current = null; }
    writeDraft();
  }, [writeDraft]);

  const handleCanvasSave = useCallback((payload) => {
    latestDraftPayloadRef.current = payload;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(flushDraft, 2000);
    if (!draftMaxWaitRef.current) {
      draftMaxWaitRef.current = setTimeout(flushDraft, 30000);
    }
  }, [flushDraft]);

  // Flush a pending draft on unmount so navigating away mid-burst still persists
  useEffect(() => () => flushDraft(), [flushDraft]);

  const { init, editor } = useCanvasEditor({
    defaultState: initialDesign?.json ? JSON.stringify(initialDesign.json) : undefined,
    defaultWidth: DEFAULT_WIDTH,
    defaultHeight: DEFAULT_HEIGHT,
    clearSelectionCallback: onClearSelection,
    saveCallback: handleCanvasSave,
  });

  // ---- Bar 0 + the unsaved-work guard (owner directive, 2026-07-21) ----
  // Save/Print moved into the app bar (RibbonMount → AppBar); the floating
  // bottom-right Save Design button is GONE. Dirty truth lives in the undo
  // system (editor.isDirty: history position vs last-saved marker) and every
  // exit path funnels through the "Save your poster?" modal below.
  const { confirm } = useConfirm();
  const [saveBusy, setSaveBusy] = useState(false);
  const [printBusy, setPrintBusy] = useState(false);
  // { proceed } — the blocked action (page switch / reload / window close).
  const [leaveGuard, setLeaveGuard] = useState(null);
  // Continuation armed by the guard's Save choice: runs exactly once, AFTER
  // a successful library save (handleSavedToLibrary), never on cancel/fail.
  const guardAfterSaveRef = useRef(null);
  // Stable pointer to the per-render editor facade for window seams and
  // stale-proof callbacks.
  const editorGuardRef = useRef(null);
  editorGuardRef.current = editor;
  // A guard-approved unload (reload nav / close) sets this so beforeunload
  // lets exactly that unload through; any other path stays protected.
  const unloadBypassRef = useRef(false);

  // The editor facade handed to the INSERT-Y left panels (Templates / Text /
  // Media / Elements / Smart / QR). Same editor, but its add-verbs arm the
  // close-on-insert latch. Layers / Settings / Draw get the RAW editor: they
  // have no insert actions (or, for Draw, adds that must keep the drawer open).
  const insertEditor = useMemo(
    () => (editor ? withInsertLatch(editor, armPendingInsert) : editor),
    [editor, armPendingInsert],
  );

  // Static-poster layout (directive 2026-07-21): publish the canvas band (the
  // gap between the ribbon and the footer) to CSS as --cce-band-top / -bottom on
  // .cce-container, so the floating drawers sit over the canvas and never cover
  // the ribbon bars or footer. Measured from the canvas container's box; a
  // ResizeObserver re-measures on REAL container resizes (window resize, Row 2
  // mounting with a selection) — opening/closing a drawer no longer resizes the
  // container, so this never runs for that (and the poster never moves).
  useLayoutEffect(() => {
    const root = rootRef.current;
    const cont = containerRef.current;
    if (!root || !cont) return undefined;
    const measure = () => {
      const rr = root.getBoundingClientRect();
      const cr = cont.getBoundingClientRect();
      if (cr.height === 0) return; // not laid out yet — keep the CSS fallback
      root.style.setProperty('--cce-band-top', `${Math.max(0, Math.round(cr.top - rr.top))}px`);
      root.style.setProperty('--cce-band-bottom', `${Math.max(0, Math.round(rr.bottom - cr.bottom))}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(cont);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Close-on-insert (directive 2026-07-21). A 'object:added' that follows an
  // armed insert = the object landed → close the left drawer. Async inserts
  // (media / icon / svg / smart-object) and template loadJson all land here;
  // load / undo / redo / recolor come through the real editor, never arm the
  // latch, and so leave the open drawer alone.
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return undefined;
    const onObjectAdded = () => {
      if (!pendingInsertRef.current) return;
      pendingInsertRef.current = false;
      if (pendingInsertTimerRef.current) {
        clearTimeout(pendingInsertTimerRef.current);
        pendingInsertTimerRef.current = null;
      }
      setActiveTool(TOOLS.SELECT);
    };
    canvas.on('object:added', onObjectAdded);
    return () => canvas.off('object:added', onObjectAdded);
  }, [editor]);

  // Clear any pending insert-latch safety timer on unmount (mount-once so it
  // never clears a legitimately in-flight async insert's timer).
  useEffect(() => () => {
    if (pendingInsertTimerRef.current) clearTimeout(pendingInsertTimerRef.current);
  }, []);

  const onChangeTool = useCallback((tool) => {
    if (!editor) return;

    if (tool === TOOLS.DRAW) {
      editor?.enableDrawingMode();
    }
    if (activeTool === TOOLS.DRAW) {
      editor?.disableDrawingMode();
    }
    if (tool === activeTool) {
      setActiveTool(TOOLS.SELECT);
      return;
    }
    setActiveTool(tool);
  }, [activeTool, editor]);

  // The ribbon's contextual "Edit <recipe>" button (ui.openSmartPanel) routes
  // here. Smart Tools epic 2026-07-23: a selected smart object now opens the
  // SmartToolModal in EDIT mode (the drawer's edit form is retired); with no
  // smart selection (defensive — the button only shows with one) fall back to
  // opening the Smart Objects drawer. Gated like the sidebar entry.
  const canUseSmartObjects = hasFeature('print_editor', TOOL_FEATURE_MAP[TOOLS.SMART_OBJECTS]);
  const openSmartPanel = useCallback(() => {
    if (editor?.getSelectedSmartObject?.()?.recipe) {
      openSmartToolModal({ mode: 'edit' });
      return;
    }
    if (activeTool === TOOLS.DRAW) {
      editor?.disableDrawingMode();
    }
    setActiveTool(TOOLS.SMART_OBJECTS);
  }, [activeTool, editor]);

  // Initialize canvas
  useEffect(() => {
    const canvas = new Canvas(canvasRef.current, {
      controlsAboveOverlay: true,
      preserveObjectStacking: true,
      // Right-click context menu + middle-mouse pan need these events
      fireRightClick: true,
      fireMiddleClick: true,
      stopContextMenu: true,
    });
    // Brightivity test hook (scripts/brightivity-web-designsteps.mjs): the fabric canvas on its element.
    canvasRef.current.__bvCanvas = canvas;

    init({
      initialCanvas: canvas,
      initialContainer: containerRef.current,
    });

    return () => {
      // v6: dispose() is async. Swallow the promise but keep StrictMode's
      // mount→cleanup→remount cycle safe: a second dispose on an already
      // disposing canvas rejects — that must not surface as an unhandled
      // rejection during dev double-mounting.
      canvas.dispose().catch(() => {});
    };
  }, [init]);

  // DEV ONLY: expose the live editor facade for the automated harnesses
  // (verify-text-inspector exercises loadJson rehydration + async mutator
  // ordering, which aren't reachable through the raw canvas hook). The
  // facade is rebuilt per render, so keep the pointer fresh.
  useEffect(() => {
    if (import.meta.env.DEV) window.__cceEditor = editor;
  }, [editor]);

  // ---- Right-hand Inspector (Effects / Position / Styles) ----
  const [inspectorTab, setInspectorTab] = useState(null);
  // Ribbon drawer-link accordion targeting (R1): a Row-2 group's "…" names
  // the accordion it unfolds into ({id, n} — the nonce re-fires repeats).
  // Only the text Style tab has accordions; other tabs ignore it.
  const [inspectorSectionReq, setInspectorSectionReq] = useState(null);
  const selectionCount = editor?.selectedObjects?.length || 0;
  // PINNED MODEL (owner, 2026-07-23: "There needs to be a mechanism to only
  // show that bar if a user clicks on an advanced button" — on small screens
  // the auto-appearing drawer ate a third of the canvas). The panel NEVER
  // opens itself: an explicit action (the ribbon's More-options button, a
  // flyout footer deep-link, Position) opens it AND pins it; ✕/Esc unpins.
  // While pinned it follows selections (opens contextually, still after the
  // dblclick-guard delay); unpinned it stays out of the way entirely.
  const inspectorPinned = useRef(false);
  const closeInspector = useCallback(() => {
    inspectorPinned.current = false;
    setInspectorTab(null);
  }, []);
  const openInspectorPinned = useCallback((tab = 'effects') => {
    inspectorPinned.current = true;
    setInspectorTab((t) => t ?? tab);
  }, []);
  useEffect(() => {
    if (selectionCount === 0) {
      if (inspectorTab) setInspectorTab(null); // selection-contextual panel
      return undefined;
    }
    const first = editor?.selectedObjects?.[0];
    if (
      inspectorTab ||
      !inspectorPinned.current ||
      !first ||
      // Transient table-cell edit overlay (excludeFromExport marks it):
      // throwaway object, styling it would be silently discarded
      first.excludeFromExport
    ) {
      return undefined;
    }
    // Delay past the double-click window: opening the panel re-fits the
    // canvas, which would move the object between the two clicks of
    // dblclick-to-edit and make the second click miss.
    const timer = setTimeout(() => {
      if (first.isEditing) return; // dblclick won — the exit handler opens it
      setInspectorTab('effects');
    }, 320);
    return () => clearTimeout(timer);
  }, [selectionCount, editor?.selectedObjects, inspectorTab, editor]);

  // When a typing session ends, a PINNED panel we deferred (above) opens —
  // the object is still selected and the user is back to styling.
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return undefined;
    const onEditExit = (e) => {
      const t = e?.target;
      if (!t || !isTextType(t.type) || t.excludeFromExport) return;
      if (!inspectorPinned.current) return;
      if (canvas.getActiveObject() !== t) return;
      setInspectorTab((tab) => tab ?? 'effects');
    };
    canvas.on('text:editing:exited', onEditExit);
    return () => canvas.off('text:editing:exited', onEditExit);
  }, [editor?.canvas]);

  // ---- Right-click context menu ----
  const [ctxMenu, setCtxMenu] = useState(null);
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return;
    const onMouseDown = (opt) => {
      if (opt.e.button === 2) {
        opt.e.preventDefault();
        const target = opt.target && opt.target.name !== 'clip' ? opt.target : null;
        if (target) {
          const active = canvas.getActiveObjects();
          if (!active.includes(target)) {
            canvas.discardActiveObject();
            canvas.setActiveObject(target);
            canvas.requestRenderAll();
          }
        }
        setCtxMenu({ x: opt.e.clientX, y: opt.e.clientY, hasTarget: !!target });
      } else {
        setCtxMenu(null);
      }
    };
    canvas.on('mouse:down', onMouseDown);
    return () => canvas.off('mouse:down', onMouseDown);
  }, [editor?.canvas]);

  // ---- Double-click a placed 3D text → edit modal (recipe re-bake) ----
  // A text3d object is a plain baked image carrying its recipe in data;
  // dblclick reopens the SAME 3D panel seeded from those params. The state
  // clears if the object leaves the canvas while the modal is up (delete /
  // undo replay both fire object:removed) — never edit a ghost.
  // Emboss text shares the same recipe-image dblclick contract as text3d — its
  // own modal opens seeded from data.params (EmbossEditModal, UI lane).
  // SMART OBJECTS (Smart Tools epic, 2026-07-23): any other registered-recipe
  // object dblclicks into the SmartToolModal (edit mode) — UNLESS the canvas
  // hook's own dblclick handler just started a live table-cell / param-text
  // edit session (it runs first — registered at init — and synchronously sets
  // canvas.__cceCellEditActive; dblclick on a fraction's numerator must keep
  // inline-editing the number, not open the modal). Locked objects are left
  // alone, same as the cell editor.
  const [text3dEdit, setText3dEdit] = useState(null);
  const [embossEdit, setEmbossEdit] = useState(null);
  useEffect(() => {
    const canvas = editor?.canvas;
    if (!canvas) return undefined;
    const onDblClick = (opt) => {
      const t = opt?.target;
      if (!t) return;
      if (t.data?.tool === 'text3d') setText3dEdit(t);
      else if (t.data?.tool === 'emboss-text') setEmbossEdit(t);
      else if (
        canUseSmartObjects
        && t.locked !== true
        && t.data?.tool && t.data?.params
        && getRecipe(t.data.tool)
        && !canvas.__cceCellEditActive
      ) {
        openSmartToolModal({ mode: 'edit' });
      }
    };
    const onRemoved = (opt) => {
      setText3dEdit((cur) => (cur && opt?.target === cur ? null : cur));
      setEmbossEdit((cur) => (cur && opt?.target === cur ? null : cur));
    };
    canvas.on('mouse:dblclick', onDblClick);
    canvas.on('object:removed', onRemoved);
    return () => {
      canvas.off('mouse:dblclick', onDblClick);
      canvas.off('object:removed', onRemoved);
    };
  }, [editor?.canvas, canUseSmartObjects]);

  // ---- Drop files anywhere on the canvas (placed at the drop point) ----
  const handleCanvasDragOver = useCallback((e) => {
    if (e.dataTransfer?.types?.includes?.('Files')) e.preventDefault();
  }, []);
  const handleCanvasDrop = useCallback((e) => {
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0 || !editor) return;
    e.preventDefault();
    const at = editor.clientToScene?.(e.clientX, e.clientY) || undefined;
    importFilesToEditor(files, editor, { toast, at });
  }, [editor, toast]);

  // Preload the bundled editor fonts, then repaint so any text that rendered
  // before a face finished loading is re-measured with the real font.
  const fontsPreloaded = useRef(false);
  useEffect(() => {
    if (!editor || fontsPreloaded.current) return;
    fontsPreloaded.current = true;
    loadEditorFonts().then(() => {
      editor.canvas?.requestRenderAll();
    });
  }, [editor]);

  // Load a pending image (e.g. from scan) into the editor once it's ready
  const pendingImageLoaded = useRef(false);
  useEffect(() => {
    if (editor && initialDesign?.imageUrl && !pendingImageLoaded.current) {
      pendingImageLoaded.current = true;
      editor.addImage(initialDesign.imageUrl);
    }
  }, [editor, initialDesign?.imageUrl]);

  // ---- Draft recovery offer ----
  // On mount, check for an unsaved draft: a fresh editor offers any draft;
  // editing a library design only offers a draft belonging to THAT design.
  const [draftOffer, setDraftOffer] = useState(null);
  const draftChecked = useRef(false);
  useEffect(() => {
    if (!editor || draftChecked.current) return;
    draftChecked.current = true;
    platform.editorDraftLoad().then((res) => {
      const draft = res?.draft;
      if (!draft?.json) return;
      const relevant = initialDesign?.designId
        ? draft.designId === initialDesign.designId
        : true;
      if (relevant) setDraftOffer(draft);
    }).catch(() => {});
  }, [editor, initialDesign]);

  const restoreDraft = useCallback(() => {
    if (!editor || !draftOffer) return;
    editor.loadJson(draftOffer.json);
    draftMetaRef.current = {
      designName: draftOffer.designName || draftMetaRef.current.designName,
      designId: draftOffer.designId ?? draftMetaRef.current.designId,
    };
    // Keep the draft file: if the app dies again before a manual save, the
    // work is still recoverable. It clears on the next successful save.
    setDraftOffer(null);
  }, [editor, draftOffer]);

  const discardDraft = useCallback(() => {
    platform.editorDraftClear().catch(() => {});
    setDraftOffer(null);
  }, []);

  // Called after EVERY successful library save (the name modal's flow and
  // Bar 0's quiet save both land here): remember the saved state (suppresses
  // redundant drafts), clear the recovery slot, mark the undo position clean
  // (Save dot off) and run a pending leave-guard continuation exactly once.
  const handleSavedToLibrary = useCallback(({ json, designId, designName }) => {
    lastSavedJsonRef.current = JSON.stringify(json);
    draftMetaRef.current = {
      designName: designName || draftMetaRef.current.designName,
      designId: designId ?? draftMetaRef.current.designId,
    };
    setDraftOffer(null);
    platform.editorDraftClear().catch(() => {});
    editorGuardRef.current?.markSaved?.();
    const cont = guardAfterSaveRef.current;
    guardAfterSaveRef.current = null;
    if (cont) {
      unloadBypassRef.current = true; // continuation may be a reload/close
      cont();
    }
  }, []);

  // ---- Bar 0: Save (existing semantics — first save opens the name modal,
  // later saves quiet-save under the same name via the same updateDesign IPC
  // and the same payload shape the modal writes) ----
  const quietSave = useCallback(async () => {
    const ed = editorGuardRef.current;
    const { designId, designName } = draftMetaRef.current;
    if (!ed || !designId) return false;
    setSaveBusy(true);
    try {
      // Self-contained saves: embed remote images (Pexels/stickers) so the
      // design reloads offline — the modal's exact pre-save step.
      await flattenRemoteImages(ed.canvas);
      const json = ed.toJSON();
      const thumbnailData = ed.toDataURL({ pixelRatio: 1 });
      const widthPx = ed.getWidth();
      const heightPx = ed.getHeight();
      const metaData = {
        url: 'designEditor',
        platform: 'Design Editor',
        source: 'manual',
        dimensions: { width: widthPx, height: heightPx, unit: 'px' },
        additionalInfo: {
          widthInches: widthPx / 72,
          heightInches: heightPx / 72,
          actualDPI: 72,
          actualPixelWidth: widthPx,
          actualPixelHeight: heightPx,
        },
      };
      if (!platform.updateDesign) {
        guardAfterSaveRef.current = null;
        toast.showError('Save functionality not available');
        return false;
      }
      // Brightivity: the library card, Print and Send-for-approval read the full picture, so a quiet
      // save refreshes it too (a failed export still saves the JSON + thumbnail).
      let fullResBlob = null;
      try {
        const exported = await exportWorkspaceBlob(ed.canvas, ed.getWorkspace(), ed.getExportDPI?.() ?? 300);
        fullResBlob = exported?.blob || null;
      } catch (err) {
        console.warn('[editor] full-res export failed on quiet save:', err?.message);
      }
      const result = await platform.updateDesign(designId, {
        d_name: designName,
        d_thumbnail: thumbnailData,
        d_json: json,
        d_metaData: metaData,
        d_fullBlob: fullResBlob,
      });
      if (result?.success) {
        toast.showSuccess('Design saved');
        handleSavedToLibrary({ json, designId, designName });
        return true;
      }
      guardAfterSaveRef.current = null;
      toast.showError('Failed to save design: ' + (result?.error || 'unknown error'));
      return false;
    } catch (error) {
      guardAfterSaveRef.current = null;
      toast.showError('Error saving design: ' + error.message);
      return false;
    } finally {
      setSaveBusy(false);
    }
  }, [toast, handleSavedToLibrary]);

  const handleBarSave = useCallback(() => {
    if (draftMetaRef.current.designId) {
      quietSave();
    } else {
      setIsModalOpen(true); // first save: name it (the existing modal flow)
    }
  }, [quietSave]);

  // ---- Bar 0: Print — the app's ONE print path (the SaveDesignModal's
  // handleOpenInPrint recipe verbatim): low-DPI honesty gate → DPI-targeted
  // exportWorkspaceBlob at the design's sharpness setting → blob handoff →
  // navigate-to-print (App.jsx's guarded listener owns the page switch, so a
  // dirty design still gets its "Save your poster?" moment before leaving).
  const handleBarPrint = useCallback(async () => {
    const ed = editorGuardRef.current;
    if (!ed) return;
    const lowRes = ed.getLowResImages?.(150) || [];
    if (lowRes.length > 0) {
      const worst = Math.min(...lowRes.map((r) => r.dpi));
      const proceed = await confirm(
        lowRes.length === 1
          ? `One image in this design is stretched beyond its resolution (~${worst} DPI) and may print blurry. Shrink it, or print anyway?`
          : `${lowRes.length} images in this design are stretched beyond their resolution (worst ~${worst} DPI) and may print blurry. Shrink them, or print anyway?`,
        { title: 'Print quality check', confirmText: 'Print anyway', cancelText: 'Go back' }
      );
      if (!proceed) return;
    }
    setPrintBusy(true);
    try {
      const workspace = ed.getWorkspace();
      const canvasWidthPx = ed.getWidth();
      const canvasHeightPx = ed.getHeight();
      const targetDPI = ed.getExportDPI?.() ?? 300;
      const { blob, actualDPI, widthPx, heightPx } = await exportWorkspaceBlob(
        ed.canvas,
        workspace,
        targetDPI,
      );
      window.pendingBrowserDownload = {
        base64: createHandoffUrl(blob),
        blob,
        name: draftMetaRef.current.designName,
        metadata: {
          url: 'designEditor',
          platform: 'Design Editor',
          dimensions: { width: canvasWidthPx, height: canvasHeightPx, unit: 'px' },
          additionalInfo: {
            widthInches: canvasWidthPx / 72,
            heightInches: canvasHeightPx / 72,
            actualDPI,
            actualPixelWidth: widthPx,
            actualPixelHeight: heightPx,
          },
        },
      };
      window.dispatchEvent(new CustomEvent('navigate-to-print', {
        detail: { downloadData: window.pendingBrowserDownload },
      }));
    } catch (error) {
      toast.showError('Error preparing print data: ' + error.message);
    } finally {
      setPrintBusy(false);
    }
  }, [confirm, toast]);

  // Gear menu → Print jobs. The nav rail's design→print path does a full
  // reload to reset the print page's listeners — same trick here, guarded.
  // (After a reload the logs pane doesn't auto-open; the print page is the
  // reliable part, the auto-open was best-effort even in the old header.)
  const handlePrintJobsNav = useCallback(() => {
    const go = () => {
      window.dispatchEvent(new CustomEvent('bv-navigate', { detail: { to: '/print' } }));
    };
    if (editorGuardRef.current?.isDirty?.()) setLeaveGuard({ proceed: go });
    else go();
  }, []);

  // Guard seam for the app chrome while this page is mounted: App.jsx (page
  // switching + navigate-to-* events), LeftSideNavbar (its design→print
  // reload branch) and TitleBar (window close button) consult it. Reads ride
  // refs, so registering once is stale-proof.
  useEffect(() => {
    const guard = {
      isDirty: () => !!editorGuardRef.current?.isDirty?.(),
      request: (proceed) => setLeaveGuard({ proceed }),
    };
    window.__pseUnsavedGuard = guard;
    return () => {
      if (window.__pseUnsavedGuard === guard) delete window.__pseUnsavedGuard;
    };
  }, []);

  // Window close / reload while dirty → the standard beforeunload prompt.
  // Browsers show their native ask; Electron shows none but REFUSES the
  // unload — the TitleBar close button therefore routes through the guard
  // modal BEFORE closing, and guard-approved exits set the bypass so their
  // unload passes. This listener is the last-ditch net (Alt+F4, dev reload).
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (unloadBypassRef.current) return;
      if (!editorGuardRef.current?.isDirty?.()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Guard modal actions (educator words: Save / Don't save / Keep editing).
  const guardDontSave = () => {
    const proceed = leaveGuard?.proceed;
    setLeaveGuard(null);
    if (proceed) {
      unloadBypassRef.current = true;
      proceed();
    }
  };
  const guardSave = () => {
    const proceed = leaveGuard?.proceed;
    setLeaveGuard(null);
    guardAfterSaveRef.current = proceed || null;
    if (draftMetaRef.current.designId) {
      quietSave(); // success runs the continuation via handleSavedToLibrary
    } else {
      setIsModalOpen(true); // name-first save; onSaved runs the continuation
    }
  };

  return (
    <div className="cce-container" ref={rootRef}>
      {/* Sidebar */}
      <Sidebar activeTool={activeTool} onChangeTool={onChangeTool} />

      {/* Tool Sidebars */}
      <TemplatesSidebar
        editor={insertEditor}
        isOpen={activeTool === TOOLS.TEMPLATES}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <TextInsertPanel
        editor={insertEditor}
        isOpen={activeTool === TOOLS.TEXT}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <MediaSidebar
        editor={insertEditor}
        isOpen={activeTool === TOOLS.MEDIA}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <ElementsSidebar
        editor={insertEditor}
        isOpen={activeTool === TOOLS.ELEMENTS}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <SmartObjectsSidebar
        editor={insertEditor}
        isOpen={activeTool === TOOLS.SMART_OBJECTS}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <LayersSidebar
        editor={editor}
        isOpen={activeTool === TOOLS.LAYERS}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <DrawSidebar
        editor={editor}
        isOpen={activeTool === TOOLS.DRAW}
        onClose={() => {
          editor?.disableDrawingMode();
          setActiveTool(TOOLS.SELECT);
        }}
      />
      <QRCodeSidebar
        editor={insertEditor}
        isOpen={activeTool === TOOLS.QRCODE}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />
      <SettingsSidebar
        editor={editor}
        isOpen={activeTool === TOOLS.SETTINGS}
        onClose={() => setActiveTool(TOOLS.SELECT)}
      />

      {/* Main Editor Area */}
      <main className="cce-main">
        {/* THE RIBBON — both rows (CONCEPT-text-ribbon §1). Row 1 morphs by
            selection kind ("the words": text / shape / photo / icon / page
            tools); Row 2 is the universal object row. The old contextual
            toolbar is RETIRED (R2): its text branch became Row 1's text set,
            the photo machinery rides the ribbon's usePhotoActions, the
            Background strip became the page morph, icon tint the icon morph,
            Ungroup + smart-object Edit conditional Row-1 groups. */}
        <RibbonMount
          editor={editor}
          selectedObjects={editor?.selectedObjects || []}
          onOpenInspector={(tab, section) => {
            // Every ribbon-side open is an EXPLICIT user action — pin it.
            inspectorPinned.current = true;
            setInspectorTab(tab);
            if (section) setInspectorSectionReq({ id: section, n: Date.now() });
          }}
          panelOpen={!!inspectorTab}
          onTogglePanel={() => {
            if (inspectorTab) closeInspector();
            else openInspectorPinned('effects');
          }}
          onOpenSmartPanel={canUseSmartObjects ? openSmartPanel : null}
          onOpenPageSettings={() => setActiveTool(TOOLS.SETTINGS)}
          appBar={{
            // Bar 0 host bag (owner directive 2026-07-21): Save with its
            // dirty dot, the honest Print pipeline, the gear's Print jobs.
            isDirty: !!editor?.isDirty?.(),
            designName: draftMetaRef.current.designName,
            saveBusy,
            printBusy,
            onSave: handleBarSave,
            onPrint: handleBarPrint,
            onOpenPrintJobs: handlePrintJobsNav,
          }}
        />

        {draftOffer && (
          <div className="cce-draft-banner" role="alert">
            <span className="cce-draft-banner-text">
              {t('draft.banner')}
              {draftOffer.designName ? ` ${t('draft.bannerOn', { name: draftOffer.designName })}` : ''}
              {draftOffer.savedAt ? ` ${t('draft.bannerFrom', { date: formatDateTime(draftOffer.savedAt) })}` : ''}
            </span>
            <button type="button" className="cce-draft-banner-btn restore" onClick={restoreDraft}>
              {t('draft.restore')}
            </button>
            <button type="button" className="cce-draft-banner-btn" onClick={discardDraft}>
              {t('draft.discard')}
            </button>
          </div>
        )}

        {/* CANVAS VIEWPORT (RS lane): rulers + overlay scrollbars wrap the
            fabric container. The .cce-canvas-container div (ref + drag
            handlers) is UNCHANGED inside the chrome — same measuring div,
            same drop surface. Ruler on/off + units live in the gear flyout
            (viewport/canvasPrefs.js, localStorage pseCanvasPrefs.v1). */}
        <CanvasViewport
          editor={editor}
          containerRef={containerRef}
          onDragOver={handleCanvasDragOver}
          onDrop={handleCanvasDrop}
        >
          <canvas ref={canvasRef} />
        </CanvasViewport>

        <Footer editor={editor} />
      </main>

      {/* Right-hand options drawer. Now a FLOATING overlay (.cce-inspector is
          absolutely positioned, static-poster directive) — it no longer sits in
          the flex flow, so opening/closing it does NOT resize the canvas
          container and the poster stays put. Gets the real editor (its controls
          edit the selection; it has no insert actions to latch). */}
      <InspectorPanel
        editor={editor}
        selectedObjects={editor?.selectedObjects || []}
        tab={inspectorTab}
        onTabChange={setInspectorTab}
        onClose={closeInspector}
        sectionRequest={inspectorSectionReq}
      />

      <ContextMenu editor={editor} menu={ctxMenu} onClose={() => setCtxMenu(null)} />

      {/* Edit modal for placed 3D text (dblclick seam above). Lazy chunk —
          fallback null: the modal itself appears within a frame, no flash. */}
      {text3dEdit && (
        <React.Suspense fallback={null}>
          <Text3DEditModal
            editor={editor}
            object={text3dEdit}
            onClose={() => setText3dEdit(null)}
          />
        </React.Suspense>
      )}

      {/* Edit modal for placed emboss text (dblclick seam above). Same lazy
          chunk contract as the 3D modal. */}
      {embossEdit && (
        <React.Suspense fallback={null}>
          <EmbossEditModal
            editor={editor}
            object={embossEdit}
            onClose={() => setEmbossEdit(null)}
          />
        </React.Suspense>
      )}

      {/* THE Smart Tool modal host (Smart Tools epic, 2026-07-23) — mounted
          ONCE, opened cross-tree via openSmartToolModal (ribbon Edit button,
          canvas dblclick, drawer banner, the Smart Tools browser). Gets the
          INSERT-LATCHED editor so an Insert from the modal closes the left
          drawer exactly like a drawer-tile insert; Apply (regenerate) is not
          an add-verb and never trips the latch. */}
      {canUseSmartObjects && <SmartToolModalHost editor={insertEditor} />}

      {/* The floating bottom-right "Save Design" button is RETIRED (owner
          directive 2026-07-21) — Save lives in Bar 0, the app bar above the
          ribbon rows. */}

      {/* Save Modal — the FIRST save's naming step (and legacy edit-save).
          Name/id ride draftMetaRef so a design saved this session updates in
          place instead of duplicating; onSaved marks the undo position clean
          and runs any pending leave-guard continuation. */}
      {isModalOpen && createPortal(
        <SaveDesignModal
          isOpen={isModalOpen}
          onClose={() => {
            guardAfterSaveRef.current = null; // cancelled naming = stay put
            setIsModalOpen(false);
          }}
          editor={editor}
          initialDesignName={draftMetaRef.current.designName}
          initialDesignId={draftMetaRef.current.designId}
          onSaved={handleSavedToLibrary}
        />,
        document.body
      )}

      {/* "Save your poster?" — the unsaved-changes guard (owner: a modal
          warning when clicking into another part of the app with an unsaved
          design). Overlay click = Keep editing (the safe default). */}
      {leaveGuard && createPortal(
        <div
          className="cce-modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setLeaveGuard(null)}
        >
          <div className="cce-modal cce-leave-modal" role="alertdialog" aria-label="Save your poster?">
            <h2 className="cce-modal-title">Save your poster?</h2>
            <p className="cce-modal-subtitle">
              “{draftMetaRef.current.designName || 'Untitled Design'}” has changes that aren’t saved yet.
              If you leave without saving, those changes will be lost.
            </p>
            <div className="cce-modal-actions">
              <button className="cce-btn cce-btn-success" onClick={guardSave}>
                Save
              </button>
              <button className="cce-btn cce-btn-secondary" onClick={guardDontSave}>
                Don’t save
              </button>
              <button className="cce-btn cce-btn-primary" onClick={() => setLeaveGuard(null)}>
                Keep editing
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
